import { useState, useRef, useEffect, useCallback } from "react";
import { router } from "@inertiajs/react";
import I from "../../components/Icons";
import { ToastContainer } from "../../components/Toast";
import AppShell from "../../components/Shell/AppShell";
import InlineResultsSection from "../../components/products/InlineResultsSection";
import ModeConfigPanel from "../../components/products/ModeConfigPanel";
import { DimensionReviewNotice, reviewItemKey } from "../../components/products/DimensionReviewNotice";
import "../../components/products/ProductDimensions.css";
import { CONFIGURABLE_MODES, contextOptions, modes, defaultArrangementSceneKeys, estimateImageCalls, modeLabel, type ModeKey } from "../../lib/generationModes";
import { RequestError, postWithoutBody, requestJson } from "../../lib/http";
import { common, getLanguage, pick } from "../../lib/i18n";
import { featureName, fileExtension, formatBytes, isPhotoFile } from "../../lib/productPresentation";
import { formatTokensPl } from "../../lib/tokens";
import useProductParseSocket from "../../hooks/useProductParseSocket";
import { useToast } from "../../hooks/useToast";
import type { ProductChannelEvent } from "../../hooks/useProductParseSocket";
import type { ActiveGenerationJob, AgeBand, BodyFocus, CurrentUser, DimensionReview, FeatureFlags, GenerationOutputUpdate, HairStyling, HandsStyling, PersonaPresentation, Product, ProductFileRole, ProductSummary } from "../../types";

type BrandOption = { id: number; name: string };
type PersonaOption = { id: number; name: string };

type UrlPreviewImage = { url: string; thumb_url: string; likely_other_product: boolean };

type UrlPreview = {
  url: string;
  name: string | null;
  description: string | null;
  text_preview: string;
  has_text: boolean;
  images: UrlPreviewImage[];
};
type SelectOption = { id: string; label: string };

type Props = {
  product: Product;
  products: ProductSummary[];
  current_user: CurrentUser;
  brands: BrandOption[];
  model_personas: PersonaOption[];
  image_aspect_ratios: SelectOption[];
  image_sizes: SelectOption[];
  default_aspect_ratio: string;
  default_image_size: string;
  feature_flags?: FeatureFlags;
};

type GenerationJobCreateResponse = {
  job_id?: number;
  outputs?: GenerationOutputUpdate[];
};

type PersonaMode = "none" | "saved" | "inline";

const PERSONA_AGE_BANDS: AgeBand[] = ["18_25", "25_35", "35_50", "50_65", "65_plus"];
const PERSONA_PRESENTATIONS: PersonaPresentation[] = ["female", "male", "neutral", "unspecified"];
const PERSONA_HANDS_STYLINGS: HandsStyling[] = ["natural_short", "glossy_manicure", "bare"];
const PERSONA_HAIR_STYLINGS: HairStyling[] = ["up", "down", "pulled_back"];
const PERSONA_BODY_FOCUSES: BodyFocus[] = ["finger", "ear", "wrist", "neck_decolletage", "full"];
const PERSONA_APPEARANCE_NOTES_MAX_LENGTH = 1000;

const Chk = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8.5l3.5 3.5 6.5-7"/>
  </svg>
);

const DIMENSION_KEY_OPTIONS_COPY = {
  pl: [
    { value: "height_cm",      label: "Wysokość (cm)" },
    { value: "width_cm",       label: "Szerokość (cm)" },
    { value: "depth_cm",       label: "Głębokość (cm)" },
    { value: "length_cm", label: "Długość (cm)" },
    { value: "diameter_mm", label: "Średnica (mm)" },
    { value: "thickness_mm", label: "Grubość (mm)" },
    { value: "weight_kg",      label: "Waga (kg)" },
    { value: "max_load_kg",    label: "Maks. obciążenie (kg)" },
  ],
  en: [
    { value: "height_cm",      label: "Height (cm)" },
    { value: "width_cm",       label: "Width (cm)" },
    { value: "depth_cm",       label: "Depth (cm)" },
    { value: "length_cm", label: "Length (cm)" },
    { value: "diameter_mm", label: "Diameter (mm)" },
    { value: "thickness_mm", label: "Thickness (mm)" },
    { value: "weight_kg",      label: "Weight (kg)" },
    { value: "max_load_kg",    label: "Max load (kg)" },
  ],
};

const dimensionKeyOptions = () => pick(DIMENSION_KEY_OPTIONS_COPY);

const humanizeDimensionKeyLabel = (key: string): string => {
  const base = key.replace(/_(mm|cm|m|in|kg|g)$/, "").replace(/_/g, " ").trim();
  if (!base) return key;
  const label = base.charAt(0).toUpperCase() + base.slice(1);
  return label;
};

const dimensionKeyLabel = (key: string): string =>
  dimensionKeyOptions().find((o) => o.value === key)?.label ?? humanizeDimensionKeyLabel(key);

const normalizeCustomDimensionKey = (raw: string, defaultUnit: "mm" | "cm"): string | null => {
  const slug = raw
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/ł/g, "l")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!slug) return null;
  const withSuffix = /_(mm|cm|m|in|kg|g)$/.test(slug) ? slug : `${slug}_${defaultUnit}`;
  return withSuffix.replace(/_+/g, "_");
};

const pluralPl = (count: number, forms: [string, string, string]): string => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (count === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
};

const graphicsEstimateLabel = (count: number): string => {
  if (getLanguage() === "en") {
    return count === 1 ? "1 image" : `Approx. ${count} images in the current configuration`;
  }
  return count === 1 ? "1 grafika" : `Szacunkowo ${count} ${pluralPl(count, ["grafika", "grafiki", "grafik"])} w bieżącej konfiguracji`;
};

const copy = {
  pl: {
    step: "KROK", ready: "gotowe", edit: "Edytuj", product: "Produkt", visuals: "Grafiki", results: "Wyniki",
    deleteProduct: "Usuń produkt",
    confirmDeleteProduct: (n: string) => `Usunąć produkt „${n}" wraz ze wszystkimi plikami i generacjami?`,
    uploadTitle: "Wrzuć dane produktu", uploadSubtitle: "Excel z kartą produktu + zdjęcia lub ZIP z katalogiem",
    filesSeparately: "Pliki osobno", dropZip: "Przeciągnij ZIP lub kliknij",
    urlTab: "Link", urlPlaceholder: "https://sklep.example/produkt/fotel-axis",
    urlFetch: "Pobierz ze strony", urlFetching: "Pobieram stronę…",
    urlFound: (n: number) => `Znaleziono ${n} zdjęć - zaznacz te, które chcesz zaimportować`,
    urlNoImages: "Nie znalazłem zdjęć na tej stronie. Strona może ładować galerię przez JavaScript - wgraj pliki ręcznie.",
    urlIncludeText: "Dołącz opis ze strony jako dane produktu",
    urlImport: "Importuj zaznaczone", urlImporting: "Importuję…",
    urlImportStarted: "Import ruszył - pliki pojawią się za chwilę.",
    urlNothingSelected: "Zaznacz przynajmniej jedno zdjęcie lub dołącz opis ze strony.",
    urlRights: "Importuj wyłącznie treści, do których masz prawa.",
    urlSelectAll: "Zaznacz wszystkie", urlClearAll: "Odznacz wszystkie",
    urlOtherProduct: "Inny produkt?",
    urlOtherProductHint: "To zdjęcie może pochodzić z innego produktu na tej stronie - odznaczone domyślnie, sprawdź przed importem.",
    urlThumbUnavailable: "Podgląd niedostępny",
    dropZipHint: "ZIP z plikami Excel, PDF i zdjęciami - auto-rozpakowanie", filesAfterUnzip: "Pliki po rozpakowaniu",
    noDataInZip: "Brak plików danych w ZIP.", preview: "Podgląd", noPhotosInZip: "Brak zdjęć w ZIP.",
    photosWarn: (n: number) => `${n} zdjęć - więcej zdjęć wydłuża czas generowania`,
    productFiles: "Pliki produktu", uploadFile: "Wgraj plik", addFile: "Dodaj plik", productPhotos: "Zdjęcia produktu",
    uploadPhotos: "Wgraj zdjęcia", uploading: "Przesyłanie plików…",
    setScaleReference: "Ustaw jako referencję skali", clearScaleReference: "Usuń jako referencję skali (kliknij, aby cofnąć)",
    scaleReferenceHint: "Ikona linijki na zdjęciu - referencja skali dla trybu Lifestyle (jedna na produkt).",
    extractingZip: "Rozpakowuję ZIP…", importingFiles: "Importuję pliki ze strony…",
    analyzing: "Analizuję…", analyzeAgain: "Analizuj ponownie", analyze: "Analizuj",
    lastAnalysis: (tk: string, m: string) => `Ostatnia analiza: ${tk} tokenów (${m})`,
    usesGeminiTokens: "Zużywa tokeny Gemini", nextProductCard: "Dalej - karta produktu", nextChooseVisuals: "Dalej - wybierz grafiki",
    filesUploaded: "Pliki wgrane", photosCount: (n: number) => `${n} zdjęć`,
    productLocked: "Karta produktu - najpierw wgraj i przeanalizuj pliki produktu",
    reviewProductCard: "Sprawdź kartę produktu", reviewSubtitle: "AI wyciągnęło dane - możesz je edytować przed generacją",
    name: "Nazwa", dimensions: "Wymiary", variants: "Warianty", certificates: "Certyfikaty", features: "Funkcje",
    deleteDimension: "Usuń wymiar", chooseDimension: "Wybierz część produktu…", customKey: "Inny wymiar…", customKeyPlaceholder: "np. handle_width_mm",
    invalidDimensionKey: "Podaj nazwę części i wymiaru, np. handle_width_mm. Jawna jednostka zostanie zachowana; bez jednostki przyjmujemy cm.",
    valuePlaceholder: "np. 4 mm", addShort: "Dodaj", addVariant: "+ wariant", addCert: "+ certyfikat", addFeature: "+ funkcja",
    productDescription: "Opis produktu (edytowalny)", descPlaceholder: "Opis produktu zostanie wyciągnięty po parsowaniu…",
    fingerprintHint: "(ze zdjęć produktu, ground truth dla generacji)",
    analyzeStartFailed: "Nie udało się uruchomić analizy.", analyzePhotosAgain: "Analizuj zdjęcia ponownie",
    saveFailed: "Nie udało się zapisać.", saving: "Zapisuję…", save: "Zapisz", cancel: "Anuluj",
    lastTime: (d: string, extra: string) => `Ostatnio: ${d}${extra}`,
    tokensExtra: (tk: string, m: string) => ` · ${tk} tokenów (${m})`,
    analyzingPhotos: "Analizuję zdjęcia produktu…",
    noFingerprint: "Brak fingerprintu. Kliknij „Analizuj zdjęcia ponownie” po wgraniu zdjęć produktu.",
    photoAnalyzeFailed: "Nie udało się przeanalizować zdjęć.", zipError: "Błąd podczas rozpakowywania ZIP.",
    generationSettings: "Ustawienia generacji", productCategory: "Kategoria produktu",
    customCategoryPlaceholder: "Własny opis kategorii po angielsku…", captionsOnBoards: "Napisy na planszach",
    generateCaptions: "Generuj podpisy (domyślnie: bez podpisów)", fontPlaceholder: "Czcionka (np. Inter, Montserrat)",
    imageOrientation: "Orientacja obrazu", qualityResolution: "Jakość / rozdzielczość", aiQualityControl: "Kontrola jakości AI",
    qualityControlHint: "Ocena każdej grafiki przez AI to dodatkowe wywołanie modelu - wyłącz, gdy nie potrzebujesz automatycznej kontroli.",
    brand: "Marka", noBrand: "(bez marki)", includeBrandLogo: "Umieść logo marki",
    modelPersona: "Persona modela", noModelPersona: "(bez persony)",
    personaModeNone: "Bez persony", personaModeSaved: "Zapisana persona", personaModeInline: "Persona jednorazowa",
    personaSavedHint: "Wybierz zapisaną personę z listy.",
    personaInlineHint: "Zdefiniuj personę tylko na potrzeby tej generacji - nie zostanie zapisana.",
    personaSkinTone: "Odcień skóry (skala Google Monk)", personaAgeBand: "Przedział wiekowy", personaPresentation: "Prezentacja",
    personaHandsStyling: "Stylizacja dłoni", personaHairStyling: "Stylizacja włosów", personaBodyFocus: "Kadr / focus",
    personaAppearanceNotesOpt: "Dodatkowe cechy wyglądu (opcjonalne)",
    personaAppearanceNotesPlaceholder: "Neutralne deskryptory wyglądu, np. smukłe dłonie, krótkie paznokcie…",
    ageBandLabels: { "18_25": "18–25 lat", "25_35": "25–35 lat", "35_50": "35–50 lat", "50_65": "50–65 lat", "65_plus": "65+ lat" } as Record<string, string>,
    presentationLabels: { female: "Kobieca", male: "Męska", neutral: "Neutralna", unspecified: "Nieokreślona" } as Record<string, string>,
    handsStylingLabels: { natural_short: "Naturalne, krótkie paznokcie", glossy_manicure: "Błyszczący manicure", bare: "Bez stylizacji" } as Record<string, string>,
    hairStylingLabels: { up: "Upięte", down: "Rozpuszczone", pulled_back: "Związane z tyłu" } as Record<string, string>,
    bodyFocusLabels: { finger: "Dłoń / palec", ear: "Ucho", wrist: "Nadgarstek", neck_decolletage: "Szyja / dekolt", full: "Cała sylwetka" } as Record<string, string>,
    saveChanges: "Zapisz zmiany", chooseVisuals: "Wybierz grafiki", productWord: "Produkt",
    variantsCount: (n: number) => `${n} wariantów`,
    modesLockedReview: "Typy grafik - najpierw sprawdź i zatwierdź kartę produktu",
    modesLockedUpload: "Typy grafik - najpierw wgraj dane produktu",
    whatGenerate: "Co chcesz wygenerować?", modesSubtitle: "Zaznacz typy grafik - wygenerujemy wszystkie naraz",
    selectAll: "Wszystkie", deselect: "Odznacz",
    tokenHint: "Mniej trybów naraz, mniej scen i wariantów oraz opcja „Pomiń planszę\" zmniejszają zużycie tokenów.",
    lastSuggestion: (tk: string, m: string) => `Ostatni dobór: ${tk} tokenów (${m})`,
    suggestingAI: "Dobiera AI…", suggestScenesAI: "Dobierz scenerie AI", modeConfiguration: "Konfiguracja trybów",
    inheritOrientation: "- jak w ustawieniach ogólnych - (orientacja)", inheritQuality: "- jak w ustawieniach ogólnych - (jakość)",
    previousGenerations: "POPRZEDNIE GENERACJE", deleteGeneration: "Usuń generację",
    confirmDeleteGeneration: "Usunąć tę generację wraz z obrazami?", readyToGenerate: "Gotowy do generowania",
    starting: "Uruchamiam…", generate: "Generuj",
    genBarSummary: (types: number, imgs: number) => `${types} ${types === 1 ? "typ" : types < 5 ? "typy" : "typów"} grafik · ≈ ${imgs} ${pluralPl(imgs, ["obraz", "obrazy", "obrazów"])}`,
    selectedLabel: "Wybrano:", ofWord: "z", selectedOf: (sel: number, total: number) => `Wybrano: ${sel} z ${total}`,
  },
  en: {
    step: "STEP", ready: "ready", edit: "Edit", product: "Product", visuals: "Visuals", results: "Results",
    deleteProduct: "Delete product",
    confirmDeleteProduct: (n: string) => `Delete product "${n}" with all its files and generations?`,
    uploadTitle: "Add product data", uploadSubtitle: "Excel with the product card + photos, or a ZIP with the catalog",
    filesSeparately: "Files separately", dropZip: "Drag a ZIP or click",
    urlTab: "Link", urlPlaceholder: "https://shop.example/product/axis-chair",
    urlFetch: "Fetch from page", urlFetching: "Fetching page…",
    urlFound: (n: number) => `Found ${n} images - select the ones you want to import`,
    urlNoImages: "I found no images on this page. The gallery may be loaded via JavaScript - upload the files manually.",
    urlIncludeText: "Include the page description as product data",
    urlImport: "Import selected", urlImporting: "Importing…",
    urlImportStarted: "Import started - the files will show up shortly.",
    urlNothingSelected: "Select at least one image, or include the page description.",
    urlRights: "Only import content you have the rights to.",
    urlSelectAll: "Select all", urlClearAll: "Clear selection",
    urlOtherProduct: "Different product?",
    urlOtherProductHint: "This image may belong to a different product on this page - deselected by default, review before importing.",
    urlThumbUnavailable: "Preview unavailable",
    dropZipHint: "ZIP with Excel, PDF and photos - auto-extracted", filesAfterUnzip: "Files after extraction",
    noDataInZip: "No data files in the ZIP.", preview: "Preview", noPhotosInZip: "No photos in the ZIP.",
    photosWarn: (n: number) => `${n} photos - more photos increase generation time`,
    productFiles: "Product files", uploadFile: "Upload file", addFile: "Add file", productPhotos: "Product photos",
    uploadPhotos: "Upload photos", uploading: "Uploading files…",
    setScaleReference: "Set as scale reference", clearScaleReference: "Remove as scale reference (click to undo)",
    scaleReferenceHint: "Ruler icon on a photo - scale reference for Lifestyle mode (one per product).",
    extractingZip: "Extracting ZIP…", importingFiles: "Importing files from the page…",
    analyzing: "Analyzing…", analyzeAgain: "Analyze again", analyze: "Analyze",
    lastAnalysis: (tk: string, m: string) => `Last analysis: ${tk} tokens (${m})`,
    usesGeminiTokens: "Uses Gemini tokens", nextProductCard: "Next - product card", nextChooseVisuals: "Next - choose visuals",
    filesUploaded: "Files uploaded", photosCount: (n: number) => `${n} photos`,
    productLocked: "Product card - first upload and analyze the product files",
    reviewProductCard: "Review the product card", reviewSubtitle: "The AI extracted the data - you can edit it before generating",
    name: "Name", dimensions: "Dimensions", variants: "Variants", certificates: "Certificates", features: "Features",
    deleteDimension: "Remove dimension", chooseDimension: "Choose product part…", customKey: "Other measurement…", customKeyPlaceholder: "e.g. handle_width_mm",
    invalidDimensionKey: "Name the component and measurement, e.g. handle_width_mm. Explicit units are preserved; without a unit, cm is used.",
    valuePlaceholder: "e.g. 4 mm", addShort: "Add", addVariant: "+ variant", addCert: "+ certificate", addFeature: "+ feature",
    productDescription: "Product description (editable)", descPlaceholder: "The product description will be extracted after parsing…",
    fingerprintHint: "(from product photos, ground truth for generation)",
    analyzeStartFailed: "Could not start the analysis.", analyzePhotosAgain: "Analyze photos again",
    saveFailed: "Could not save.", saving: "Saving…", save: "Save", cancel: "Cancel",
    lastTime: (d: string, extra: string) => `Last: ${d}${extra}`,
    tokensExtra: (tk: string, m: string) => ` · ${tk} tokens (${m})`,
    analyzingPhotos: "Analyzing product photos…",
    noFingerprint: "No fingerprint. Click \"Analyze photos again\" after uploading product photos.",
    photoAnalyzeFailed: "Could not analyze the photos.", zipError: "Error while extracting the ZIP.",
    generationSettings: "Generation settings", productCategory: "Product category",
    customCategoryPlaceholder: "Custom category description in English…", captionsOnBoards: "Captions on boards",
    generateCaptions: "Generate captions (default: no captions)", fontPlaceholder: "Font (e.g. Inter, Montserrat)",
    imageOrientation: "Image orientation", qualityResolution: "Quality / resolution", aiQualityControl: "AI quality control",
    qualityControlHint: "Scoring each visual with AI is an extra model call - turn it off when you don't need automatic control.",
    brand: "Brand", noBrand: "(no brand)", includeBrandLogo: "Place brand logo",
    modelPersona: "Model persona", noModelPersona: "(no persona)",
    personaModeNone: "No persona", personaModeSaved: "Saved persona", personaModeInline: "One-off persona",
    personaSavedHint: "Pick a saved persona from the list.",
    personaInlineHint: "Define a persona for this generation only - it will not be saved.",
    personaSkinTone: "Skin tone (Google Monk Scale)", personaAgeBand: "Age band", personaPresentation: "Presentation",
    personaHandsStyling: "Hands styling", personaHairStyling: "Hair styling", personaBodyFocus: "Framing / focus",
    personaAppearanceNotesOpt: "Additional appearance notes (optional)",
    personaAppearanceNotesPlaceholder: "Neutral appearance descriptors, e.g. slender hands, short nails…",
    ageBandLabels: { "18_25": "18–25 y/o", "25_35": "25–35 y/o", "35_50": "35–50 y/o", "50_65": "50–65 y/o", "65_plus": "65+ y/o" } as Record<string, string>,
    presentationLabels: { female: "Feminine", male: "Masculine", neutral: "Neutral", unspecified: "Unspecified" } as Record<string, string>,
    handsStylingLabels: { natural_short: "Natural, short nails", glossy_manicure: "Glossy manicure", bare: "No manicure styling" } as Record<string, string>,
    hairStylingLabels: { up: "Up", down: "Down", pulled_back: "Pulled back" } as Record<string, string>,
    bodyFocusLabels: { finger: "Hand / finger", ear: "Ear", wrist: "Wrist", neck_decolletage: "Neck / décolletage", full: "Full person" } as Record<string, string>,
    saveChanges: "Save changes", chooseVisuals: "Choose visuals", productWord: "Product",
    variantsCount: (n: number) => `${n} variants`,
    modesLockedReview: "Visual types - first review and confirm the product card",
    modesLockedUpload: "Visual types - first upload the product data",
    whatGenerate: "What do you want to generate?", modesSubtitle: "Select visual types - we generate them all at once",
    selectAll: "All", deselect: "Deselect",
    tokenHint: "Fewer modes at once, fewer scenes and variants, and the \"Skip board\" option reduce token usage.",
    lastSuggestion: (tk: string, m: string) => `Last suggestion: ${tk} tokens (${m})`,
    suggestingAI: "AI suggesting…", suggestScenesAI: "Suggest scenes with AI", modeConfiguration: "Mode configuration",
    inheritOrientation: "- same as general settings - (orientation)", inheritQuality: "- same as general settings - (quality)",
    previousGenerations: "PREVIOUS GENERATIONS", deleteGeneration: "Delete generation",
    confirmDeleteGeneration: "Delete this generation together with its images?", readyToGenerate: "Ready to generate",
    starting: "Starting…", generate: "Generate",
    genBarSummary: (types: number, imgs: number) => `${types} image ${types === 1 ? "type" : "types"} · ≈ ${imgs} ${imgs === 1 ? "image" : "images"}`,
    selectedLabel: "Selected:", ofWord: "of", selectedOf: (sel: number, total: number) => `Selected: ${sel} of ${total}`,
  },
};

/* ── Main component ───────────────────────────────────── */
export default function ProductShow({ product, products, current_user, brands, model_personas, image_aspect_ratios, image_sizes, default_aspect_ratio, default_image_size, feature_flags }: Props) {
  const t = pick(copy);
  const c = common();
  const allModes = modes();
  const { toasts, add: addToast, dismiss: dismissToast } = useToast();
  const isProductCardReviewEnabled = feature_flags?.product_card_review_enabled ?? true;
  const areBrandsEnabled = feature_flags?.brands_enabled ?? true;
  const arePersonasEnabled = feature_flags?.personas_enabled ?? false;
  const isQualityGateEnabled = feature_flags?.quality_gate_enabled ?? true;
  const isUrlImportEnabled = feature_flags?.url_import_enabled ?? true;

  /* Section states */
  const [uploadDone, setUploadDone]   = useState(!!product.parsed_at);
  const [productDone, setProductDone] = useState(!isProductCardReviewEnabled && !!product.parsed_at);
  const [uploadOpen, setUploadOpen]   = useState(!product.parsed_at);
  const [productOpen, setProductOpen] = useState(isProductCardReviewEnabled && !!product.parsed_at);
  const [modesOpen, setModesOpen]     = useState(!isProductCardReviewEnabled && !!product.parsed_at);

  /* Upload state */
  const [uploadMode, setUploadMode]   = useState<"files" | "zip" | "url">("files");
  const [pageUrl, setPageUrl]         = useState("");
  const [urlLoading, setUrlLoading]   = useState(false);
  const [urlPreview, setUrlPreview]   = useState<UrlPreview | null>(null);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [brokenThumbUrls, setBrokenThumbUrls] = useState<Set<string>>(new Set());
  const [includePageText, setIncludePageText] = useState(true);
  const [importing, setImporting]     = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [processing, setProcessing]   = useState<string | null>(null);
  const parseStorageKey = `product:${product.id}:parsing`;
  const [parsing, setParsing]         = useState(() => !product.parsed_at && window.localStorage.getItem(parseStorageKey) === "true");
  const [parseError, setParseError]   = useState<string | null>(null);

  /* Visual fingerprint */
  const [reanalyzing, setReanalyzing] = useState(false);
  const [fingerprintError, setFingerprintError] = useState<string | null>(null);
  const [fingerprintEdit, setFingerprintEdit] = useState<string | null>(null);
  const [savingFingerprint, setSavingFingerprint] = useState(false);

  /* Product card editing */
  const [descEdit, setDescEdit] = useState(product.description_md || "");
  const [nameEdit, setNameEdit] = useState(product.name || "");
  const [dimensionsEdit, setDimensionsEdit] = useState<Record<string, string>>(product.dimensions_json ?? {});
  const [reviewDimensionValues, setReviewDimensionValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((product.dimension_review ?? []).map((item) => [reviewItemKey(item), item.value])),
  );
  const [resolvedReviewKeys, setResolvedReviewKeys] = useState<Set<string>>(new Set());
  const [variantsEdit, setVariantsEdit] = useState<NonNullable<Product["variants_json"]>>(product.variants_json ?? []);
  const [certificationsEdit, setCertificationsEdit] = useState<string[]>(product.certifications_json ?? []);
  const [featuresEdit, setFeaturesEdit] = useState<string[]>((product.features_json ?? []).map(f => featureName(f)));
  const [newVariantInput, setNewVariantInput] = useState("");
  const [newCertInput, setNewCertInput] = useState("");
  const [newFeatureInput, setNewFeatureInput] = useState("");
  const [newDimKeySelect, setNewDimKeySelect] = useState<string>("");
  const [newDimCustomKey, setNewDimCustomKey] = useState("");
  const [newDimValue, setNewDimValue] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);

  useEffect(() => {
    if (!product.parsed_at) return;
    window.localStorage.removeItem(parseStorageKey);
    setParsing(false);
    setUploadDone(true);
    setUploadOpen(false);
    setProductOpen(isProductCardReviewEnabled);
    setProductDone(!isProductCardReviewEnabled);
    setModesOpen(!isProductCardReviewEnabled);
  }, [product.parsed_at, isProductCardReviewEnabled]);

  useEffect(() => {
    setDescEdit(product.description_md || "");
  }, [product.description_md]);

  useEffect(() => {
    setDimensionsEdit(product.dimensions_json ?? {});
  }, [product.dimensions_json]);

  useEffect(() => {
    setNameEdit(product.name || "");
    setVariantsEdit(product.variants_json ?? []);
    setCertificationsEdit(product.certifications_json ?? []);
    setFeaturesEdit((product.features_json ?? []).map(f => featureName(f)));
  }, [product.parsed_at]);

  /* Global settings */
  const [contextKey, setContextKey]       = useState(product.product_context_key === "custom" ? "custom" : "auto");
  const [contextCustom, setContextCustom] = useState(product.product_context_custom || "");
  const [captionsOn, setCaptionsOn]       = useState(product.captions_enabled);
  const [captionFont, setCaptionFont]     = useState(product.caption_font_family || "Inter");
  const [brandId, setBrandId]             = useState<number | null>(product.default_brand_id ?? null);
  const [includeBrandLogo, setIncludeBrandLogo] = useState(true);
  const [personaId, setPersonaId]         = useState<number | null>(null);
  const [personaMode, setPersonaMode]     = useState<PersonaMode>("none");
  const [personaSkinTone, setPersonaSkinTone]             = useState(5);
  const [personaAgeBand, setPersonaAgeBand]               = useState<AgeBand>("25_35");
  const [personaPresentation, setPersonaPresentation]     = useState<PersonaPresentation>("unspecified");
  const [personaHandsStyling, setPersonaHandsStyling]     = useState<HandsStyling>("natural_short");
  const [personaHairStyling, setPersonaHairStyling]       = useState<HairStyling>("down");
  const [personaBodyFocus, setPersonaBodyFocus]           = useState<BodyFocus>("full");
  const [personaAppearanceNotes, setPersonaAppearanceNotes] = useState("");
  const [qualityGateOn, setQualityGateOn] = useState(() => feature_flags?.quality_gate_enabled ?? true);
  const imageConfigStorageKey = `product:${product.id}:imageConfig`;
  const readStoredImageConfig = (): { aspect_ratio?: string; image_size?: string; modeConfigs?: Record<string, Record<string, string>> } => {
    try {
      const raw = window.localStorage.getItem(imageConfigStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const [globalAspectRatio, setGlobalAspectRatio] = useState(() => readStoredImageConfig().aspect_ratio ?? default_aspect_ratio);
  const [globalImageSize, setGlobalImageSize]     = useState(() => readStoredImageConfig().image_size ?? default_image_size);

  /* Modes state */
  const [selected, setSelected] = useState<Set<ModeKey>>(new Set());

  /* Per-mode config */
  const [modeConfigs, setModeConfigs] = useState<Record<string, Record<string, string>>>(() => readStoredImageConfig().modeConfigs ?? {});

  useEffect(() => {
    try {
      window.localStorage.setItem(
        imageConfigStorageKey,
        JSON.stringify({ aspect_ratio: globalAspectRatio, image_size: globalImageSize, modeConfigs }),
      );
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [imageConfigStorageKey, globalAspectRatio, globalImageSize, modeConfigs]);

  /* AI config suggestions */
  const [isSuggestingConfig, setIsSuggestingConfig] = useState(false);

  const handleSuggestConfig = async () => {
    if (isSuggestingConfig) return;
    setIsSuggestingConfig(true);
    try {
      const suggestions = await requestJson<Record<string, string>>(`/products/${product.id}/ai_config_suggestions`, { method: "POST" });
      if (suggestions.staging_scene) {
        updateModeConfig("staging", "scene_source", "custom");
        updateModeConfig("staging", "scene_custom", suggestions.staging_scene);
      }
      if (suggestions.packshot_accessories) {
        updateModeConfig("packshot", "accessories", suggestions.packshot_accessories);
      }
      if (suggestions.arrangements_scenes) {
        updateModeConfig("arrangements", "scene_source", "custom");
        updateModeConfig("arrangements", "custom_scenes", suggestions.arrangements_scenes);
      }
      if (suggestions.lifestyle_setting) {
        updateModeConfig("lifestyle", "scene_source", "custom");
        updateModeConfig("lifestyle", "scene_custom", suggestions.lifestyle_setting);
      } else if (suggestions.lifestyle_scene_key) {
        updateModeConfig("lifestyle", "scene_source", "preset");
        updateModeConfig("lifestyle", "scene_key", suggestions.lifestyle_scene_key);
      }
      router.reload({ only: ["product"] });
    } finally {
      setIsSuggestingConfig(false);
    }
  };

  /* Active generation job (inline results) */
  const [activeJob, setActiveJob] = useState<ActiveGenerationJob | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  /* Refs for scroll */
  const secUpload   = useRef<HTMLDivElement>(null);
  const secProduct  = useRef<HTMLDivElement>(null);
  const secModes    = useRef<HTMLDivElement>(null);
  const secResults  = useRef<HTMLDivElement>(null);

  const fileInputRef  = useRef<HTMLInputElement>(null);
  const zipInputRef   = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };

  const handleParseEvent = useCallback((event: ProductChannelEvent) => {
    if (event.event === "files_updated") {
      setProcessing(null);
      router.reload({ only: ["product"] });
      return;
    }

    if (event.event === "files_error") {
      setProcessing(null);
      addToast(event.error_message || t.zipError, "error");
      return;
    }

    if (event.event === "parsed") {
      window.localStorage.removeItem(parseStorageKey);
      setParsing(false);
      setParseError(null);
      setUploadDone(true);
      setUploadOpen(false);
      setProductOpen(isProductCardReviewEnabled);
      setProductDone(!isProductCardReviewEnabled);
      setModesOpen(!isProductCardReviewEnabled);
      router.reload({
        only: ["product"],
        onSuccess: () => scrollTo(isProductCardReviewEnabled ? secProduct : secModes),
      });
      return;
    }

    if (event.event === "attributes_updated") {
      router.reload({ only: ["product"] });
      return;
    }

    if (event.event === "fingerprint_updated") {
      setReanalyzing(false);
      setFingerprintError(null);
      router.reload({ only: ["product"] });
      return;
    }

    if (event.event === "fingerprint_failed") {
      setReanalyzing(false);
      setFingerprintError(event.error_message || t.photoAnalyzeFailed);
      addToast(event.error_message || t.photoAnalyzeFailed);
      return;
    }

    window.localStorage.removeItem(parseStorageKey);
    setParsing(false);
    const msg = event.error_message || c.errors.parseFailed;
    setParseError(msg);
    addToast(msg);
  }, [parseStorageKey, isProductCardReviewEnabled]);

  useProductParseSocket(product.id, handleParseEvent);

  /* Upload */
  const handleFilesUpload = (files: FileList | null, isZip = false) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const form = new FormData();
    Array.from(files).forEach((f) => form.append("product_files[]", f));
    router.post(`/products/${product.id}/product_files`, form, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => { if (isZip) setProcessing(t.extractingZip); },
      onFinish: () => setUploading(false),
    });
  };

  const updateFileRole = (fileId: number, fileRole: ProductFileRole) => {
    router.patch(
      `/products/${product.id}/product_files/${fileId}`,
      { product_file: { file_role: fileRole } },
      { preserveScroll: true }
    );
  };

  const setScaleReference = (fileId: number) => {
    const current = product.files.find((f) => f.file_role === "scale_reference");
    if (current && current.id !== fileId) {
      router.patch(
        `/products/${product.id}/product_files/${current.id}`,
        { product_file: { file_role: "photo" } },
        { preserveScroll: true, onSuccess: () => updateFileRole(fileId, "scale_reference") }
      );
    } else {
      updateFileRole(fileId, "scale_reference");
    }
  };

  const fetchPageImages = async () => {
    if (!pageUrl.trim()) return;
    setUrlLoading(true);
    setUrlPreview(null);
    setSelectedImages([]);
    setBrokenThumbUrls(new Set());
    try {
      const preview = await requestJson<UrlPreview>(`/products/${product.id}/url_import/preview`, {
        body: { url: pageUrl.trim() },
      });
      setUrlPreview(preview);
      setSelectedImages(preview.images.filter((image) => !image.likely_other_product).map((image) => image.url));
      setIncludePageText(preview.has_text);
      if (preview.images.length === 0) addToast(t.urlNoImages, "error");
    } catch (error) {
      addToast(error instanceof RequestError ? error.message : c.errors.parseFailed, "error");
    } finally {
      setUrlLoading(false);
    }
  };

  const toggleSelectedImage = (url: string) => {
    setSelectedImages((prev) => prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]);
  };

  const markThumbBroken = (url: string) => {
    setBrokenThumbUrls((prev) => prev.has(url) ? prev : new Set(prev).add(url));
  };

  const importFromUrl = async () => {
    if (!urlPreview) return;
    if (selectedImages.length === 0 && !includePageText) {
      addToast(t.urlNothingSelected, "error");
      return;
    }
    setImporting(true);
    try {
      await requestJson(`/products/${product.id}/url_import`, {
        body: { url: urlPreview.url, image_urls: selectedImages, import_text: includePageText },
      });
      addToast(t.urlImportStarted, "success");
      setProcessing(t.importingFiles);
      setUrlPreview(null);
      setSelectedImages([]);
      setPageUrl("");
      setUploadMode("files");
    } catch (error) {
      addToast(error instanceof RequestError ? error.message : c.errors.parseFailed, "error");
    } finally {
      setImporting(false);
    }
  };

  const handleParse = () => {
    setParseError(null);
    setParsing(true);
    window.localStorage.setItem(parseStorageKey, "true");
    router.post(`/products/${product.id}/parse`, {}, {
      preserveScroll: true,
      preserveState: true,
      onError: () => {
        window.localStorage.removeItem(parseStorageKey);
        setParsing(false);
        const msg = c.errors.parseStartFailed;
        setParseError(msg);
        addToast(msg);
      },
    });
  };

  const confirmUpload = () => {
    setUploadDone(true);
    setUploadOpen(false);
    setProductOpen(isProductCardReviewEnabled);
    setProductDone(!isProductCardReviewEnabled);
    setModesOpen(!isProductCardReviewEnabled);
    scrollTo(isProductCardReviewEnabled ? secProduct : secModes);
  };

  const editUpload = () => {
    setUploadOpen(true);
    setProductDone(false);
    setProductOpen(false);
    setModesOpen(false);
    scrollTo(secUpload);
  };

  const availableDimOptions = dimensionKeyOptions().filter((o) => !(o.value in dimensionsEdit));
  const selectedDimKey =
    newDimKeySelect === "" || newDimKeySelect === "__custom" || availableDimOptions.some((o) => o.value === newDimKeySelect)
      ? newDimKeySelect
      : "";

  const addDimensionRow = () => {
    const value = newDimValue.trim();
    if (!value) return;

    if (selectedDimKey === "__custom") {
      const key = normalizeCustomDimensionKey(newDimCustomKey, "cm");
      if (!key) {
        addToast(t.invalidDimensionKey, "error");
        return;
      }
      setDimensionsEdit((prev) => ({ ...prev, [key]: value }));
    } else {
      if (!selectedDimKey) return;
      setDimensionsEdit((prev) => ({ ...prev, [selectedDimKey]: value }));
    }

    setNewDimValue("");
    setNewDimCustomKey("");
    setNewDimKeySelect("");
  };

  const removeDimensionRow = (key: string) => {
    setDimensionsEdit((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const acceptDimensionReview = (item: DimensionReview) => {
    const key = normalizeCustomDimensionKey(item.field || item.source_label, "cm");
    if (!key) return;
    const value = reviewDimensionValues[reviewItemKey(item)] ?? item.value;
    setDimensionsEdit((prev) => ({ ...prev, [key]: value }));
    setResolvedReviewKeys((prev) => new Set(prev).add(reviewItemKey(item)));
  };

  const rejectDimensionReview = (item: DimensionReview) => {
    setResolvedReviewKeys((prev) => new Set(prev).add(reviewItemKey(item)));
  };

  /* Save product card */
  const saveProductCard = () => {
    setSavingDesc(true);
    router.patch(`/products/${product.id}`, {
      product: {
        name:                  nameEdit,
        description_md:        descEdit,
        product_context_key:   contextKey,
        product_context_custom: contextCustom,
        captions_enabled:      captionsOn,
        caption_font_family:   captionFont,
        default_brand_id:      areBrandsEnabled ? brandId : null,
        dimensions_json:       dimensionsEdit,
        resolved_dimension_reviews: [...resolvedReviewKeys],
        variants_json:         variantsEdit,
        certifications_json:   certificationsEdit,
        features_json:         featuresEdit,
      },
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ["product"],
      onFinish: () => setSavingDesc(false),
    });
  };

  const confirmProduct = () => {
    setProductDone(true);
    setProductOpen(false);
    setModesOpen(true);
    scrollTo(secModes);
  };

  const editProduct = () => {
    setProductOpen(true);
    setProductDone(false);
    setModesOpen(false);
    scrollTo(secProduct);
  };

  /* Modes */
  const toggleMode = (k: ModeKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const updateModeConfig = (mode: string, key: string, value: string) => {
    setModeConfigs((prev) => ({
      ...prev,
      [mode]: { ...(prev[mode] || {}), [key]: value },
    }));
  };

  const updateModeConfigInheritable = (mode: string, key: string, value: string) => {
    if (!value) {
      setModeConfigs((prev) => {
        const modeCfg = { ...(prev[mode] || {}) };
        delete modeCfg[key];
        return { ...prev, [mode]: modeCfg };
      });
      return;
    }
    updateModeConfig(mode, key, value);
  };

  const handleGenerate = async () => {
    if (selected.size === 0 || generating) return;
    setGenerating(true);
    setGenerationError(null);

    const configPerMode: Record<string, Record<string, string>> = {};
    selected.forEach((m) => {
      if (modeConfigs[m]) configPerMode[m] = modeConfigs[m];
    });

    if (selected.has("arrangements")) {
      const arrangementConfig = configPerMode.arrangements || {};
      if (arrangementConfig.scene_source !== "custom" && !arrangementConfig.scene_keys) {
        configPerMode.arrangements = { ...arrangementConfig, scene_keys: defaultArrangementSceneKeys(contextKey).join(",") };
      }
    }

    try {
      const data = await requestJson<GenerationJobCreateResponse>(`/products/${product.id}/generation_jobs`, {
        body: {
          generation_job: {
            selected_modes: Array.from(selected),
            config_json: {
              ...configPerMode,
              global: {
                product_context_key: contextKey,
                product_context_custom: contextCustom,
                captions_enabled: String(captionsOn),
                caption_font: captionFont,
                aspect_ratio: globalAspectRatio,
                image_size: globalImageSize,
                ...(isQualityGateEnabled ? { quality_gate: String(qualityGateOn) } : {}),
                ...(areBrandsEnabled && brandId ? { brand_id: String(brandId), include_brand_logo: String(includeBrandLogo) } : {}),
                ...(arePersonasEnabled && personaMode === "saved" && personaId ? { model_persona_id: String(personaId) } : {}),
                ...(arePersonasEnabled && personaMode === "inline" ? {
                  model_persona_inline: {
                    skin_tone:        String(personaSkinTone),
                    age_band:         personaAgeBand,
                    presentation:     personaPresentation,
                    hands_styling:    personaHandsStyling,
                    hair_styling:     personaHairStyling,
                    body_focus:       personaBodyFocus,
                    appearance_notes: personaAppearanceNotes,
                  },
                } : {}),
              },
            },
          },
        },
      });
      if (data.job_id && data.outputs) {
        setActiveJob({ id: data.job_id, outputs: data.outputs });
        scrollTo(secResults);
      } else {
        const msg = c.errors.generationInvalidResponse;
        setGenerationError(msg);
        addToast(msg);
      }
    } catch {
      const msg = c.errors.generationStartFailed;
      setGenerationError(msg);
      addToast(msg);
    } finally {
      setGenerating(false);
    }
  };

  /* Computed */
  const dataFiles  = product.files.filter((f) => !isPhotoFile(f.content_type));
  const photoFiles = product.files.filter((f) => isPhotoFile(f.content_type));
  const isParsed   = !!product.parsed_at;
  const canAnalyze = product.files.length > 0;

  const selectedConfigurable = Array.from(selected).filter((m): m is ModeKey => CONFIGURABLE_MODES.includes(m));

  const totalImageEstimate = Array.from(selected).reduce((sum, m) => sum + estimateImageCalls(m, modeConfigs[m], product), 0);

  /* Chip state */
  const chipUpload  = uploadDone ? "done" : uploadOpen ? "active" : "hidden";
  const chipProduct = isProductCardReviewEnabled ? productDone ? "done" : productOpen ? "active" : (uploadDone ? "inactive" : "hidden") : "hidden";
  const chipModes   = modesOpen ? "active" : (productDone ? "inactive" : "hidden");

  const userForShell = {
    name: (current_user.name || current_user.email.split("@")[0]) ?? "",
    initials: (current_user.name || current_user.email).slice(0, 2).toUpperCase(),
    role: current_user.role,
  };

  return (
    <AppShell products={products} currentProductId={product.id} user={userForShell}>
      {/* ── Topbar ── */}
      <div className="v3-topbar">
        <div className="v3-topbar-grow" />
        <div className="v3-chips">
          {chipUpload !== "hidden" && (
            <button
              className={"v3-chip" + (chipUpload === "active" ? " active" : chipUpload === "done" ? " done" : "")}
              onClick={() => scrollTo(secUpload)}
            >
              {chipUpload === "done" && <Chk />} <span>Upload</span>
            </button>
          )}
          {chipProduct !== "hidden" && (
            <button
              className={"v3-chip" + (chipProduct === "active" ? " active" : chipProduct === "done" ? " done" : "")}
              onClick={() => scrollTo(secProduct)}
            >
              {chipProduct === "done" && <Chk />} <span>{t.product}</span>
            </button>
          )}
          {chipModes !== "hidden" && (
            <button
              className={"v3-chip" + (chipModes === "active" ? " active" : "")}
              onClick={() => scrollTo(secModes)}
            >
              <span>{t.visuals}</span>
            </button>
          )}
          {activeJob && (
            <button className="v3-chip active" onClick={() => scrollTo(secResults)}>
              <span>{t.results}</span>
            </button>
          )}
        </div>
        <button
          className="v3-icon-btn"
          title={t.deleteProduct}
          onClick={() => {
            if (confirm(t.confirmDeleteProduct(product.name))) {
              router.delete(`/products/${product.id}`);
            }
          }}
        >
          {I.x}
        </button>
      </div>

      {/* ── Page ── */}
      <div className="v3-page">

        {/* ── Section 1: Upload ── */}
        <div className="v3-sec" ref={secUpload}>
          {uploadOpen && (
            <div className="v3-sec-open v3-slide-in">
              <div className="v3-sec-hd">
                <span className="v3-sec-num mono">{t.step} 01</span>
                <div>
                  <h2>{t.uploadTitle}</h2>
                  <p>{t.uploadSubtitle}</p>
                </div>
              </div>
              <div className="v3-sec-body">
                <fieldset disabled={parsing} style={{ border: "none", margin: 0, padding: 0, minWidth: 0, width: "100%", opacity: parsing ? 0.55 : 1, transition: "opacity 0.2s" }}>
                <input ref={fileInputRef} type="file" multiple style={{ display: "none" }}
                  accept=".xlsx,.pdf,.docx" onChange={(e) => handleFilesUpload(e.target.files)} />
                <input ref={zipInputRef} type="file" accept=".zip" style={{ display: "none" }}
                  onChange={(e) => handleFilesUpload(e.target.files, true)} />
                <input ref={photoInputRef} type="file" multiple accept="image/*" style={{ display: "none" }}
                  onChange={(e) => handleFilesUpload(e.target.files)} />

                {/* Upload mode toggle */}
                <div className="v3-seg" style={{ marginBottom: 14 }}>
                  <button className={"v3-seg-btn" + (uploadMode === "files" ? " active" : "")}
                    onClick={() => setUploadMode("files")}>{t.filesSeparately}</button>
                  <button className={"v3-seg-btn" + (uploadMode === "zip" ? " active" : "")}
                    onClick={() => setUploadMode("zip")}>ZIP</button>
                  {isUrlImportEnabled && (
                    <button className={"v3-seg-btn" + (uploadMode === "url" ? " active" : "")}
                      onClick={() => setUploadMode("url")}>{t.urlTab}</button>
                  )}
                </div>

                {uploadMode === "url" && isUrlImportEnabled ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="url"
                        className="v3-input"
                        style={{ flex: 1 }}
                        value={pageUrl}
                        placeholder={t.urlPlaceholder}
                        onChange={(e) => setPageUrl(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && fetchPageImages()}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={fetchPageImages}
                        disabled={urlLoading || !pageUrl.trim()}
                      >
                        {urlLoading ? t.urlFetching : t.urlFetch}
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{t.urlRights}</div>

                    {urlPreview && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {urlPreview.name && (
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{urlPreview.name}</div>
                        )}

                        {urlPreview.images.length > 0 && (
                          <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                              <div className="v3-field-label">{t.urlFound(urlPreview.images.length)}</div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedImages(urlPreview.images.map((image) => image.url))}>{t.urlSelectAll}</button>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedImages([])}>{t.urlClearAll}</button>
                              </div>
                            </div>
                            <div className="v3-photo-grid">
                              {urlPreview.images.map((image) => {
                                const on = selectedImages.includes(image.url);
                                const broken = brokenThumbUrls.has(image.url);
                                return (
                                  <button
                                    key={image.url}
                                    type="button"
                                    className="v3-pg-item"
                                    aria-pressed={on}
                                    onClick={() => toggleSelectedImage(image.url)}
                                    title={image.likely_other_product ? t.urlOtherProductHint : undefined}
                                    style={{
                                      padding: 0,
                                      border: on ? "2px solid var(--accent-ink)" : "2px solid transparent",
                                      opacity: on ? 1 : 0.45,
                                      cursor: "pointer",
                                      background: "none",
                                    }}
                                  >
                                    {broken ? (
                                      <div className="v3-pg-broken">{I.img}<span>{t.urlThumbUnavailable}</span></div>
                                    ) : (
                                      <img src={image.thumb_url} loading="lazy" onError={() => markThumbBroken(image.url)} />
                                    )}
                                    {image.likely_other_product && (
                                      <span className="v3-pg-flag">{t.urlOtherProduct}</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}

                        {urlPreview.has_text && (
                          <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={includePageText}
                              onChange={(e) => setIncludePageText(e.target.checked)}
                            />
                            <span>
                              {t.urlIncludeText}
                              <span style={{ display: "block", fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
                                {urlPreview.text_preview.slice(0, 160)}…
                              </span>
                            </span>
                          </label>
                        )}

                        <div>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={importFromUrl}
                            disabled={importing}
                          >
                            {importing ? t.urlImporting : t.urlImport}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : uploadMode === "zip" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <button type="button" className="v3-drop" onClick={() => zipInputRef.current?.click()}>
                      <div className="v3-drop-inner">
                        <div className="v3-drop-icon">{I.upload}</div>
                        <div className="v3-drop-lbl">{t.dropZip}</div>
                        <div className="v3-drop-hint">{t.dropZipHint}</div>
                      </div>
                    </button>
                    {product.files.length > 0 && (
                      <div>
                        <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.filesAfterUnzip}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: 10 }}>
                          <div>
                            {dataFiles.length > 0 ? dataFiles.map((f) => (
                              <div key={f.id} className="v3-fpill">
                                <span className={"v3-ext" + (f.filename.endsWith(".xlsx") ? " xlsx" : "")}>{fileExtension(f.filename)}</span>
                                <span className="v3-fnm">{f.filename}</span>
                                <span className="v3-fsz mono">{formatBytes(f.byte_size)}</span>
                                <button className="v3-fx" onClick={() => router.delete(`/products/${product.id}/product_files/${f.id}`)}>{I.x}</button>
                              </div>
                            )) : (
                              <div style={{ fontSize: 12, color: "var(--muted)" }}>{t.noDataInZip}</div>
                            )}
                          </div>
                          <div>
                            {photoFiles.length > 0 ? (
                              <>
                                <div className="v3-photo-grid">
                                  {photoFiles.map((photoFile) => (
                                    <div key={photoFile.id} className="v3-pg-item">
                                      {photoFile.url && <img src={photoFile.url} alt="" />}
                                      {photoFile.url && <a href={photoFile.url} target="_blank" rel="noreferrer" className="v3-pg-eye" title={t.preview}>{I.eye}</a>}
                                      <button
                                        type="button"
                                        className={"v3-pg-scale" + (photoFile.file_role === "scale_reference" ? " active" : "")}
                                        title={photoFile.file_role === "scale_reference" ? t.clearScaleReference : t.setScaleReference}
                                        aria-label={`${photoFile.file_role === "scale_reference" ? t.clearScaleReference : t.setScaleReference}: ${photoFile.filename}`}
                                        aria-pressed={photoFile.file_role === "scale_reference"}
                                        onClick={() => photoFile.file_role === "scale_reference"
                                          ? updateFileRole(photoFile.id, "photo")
                                          : setScaleReference(photoFile.id)}
                                      >
                                        {I.ruler}
                                      </button>
                                      <button className="v3-pg-del" title={c.actions.delete} aria-label={`${c.actions.delete}: ${photoFile.filename}`} onClick={() => router.delete(`/products/${product.id}/product_files/${photoFile.id}`)}>{I.x}</button>
                                    </div>
                                  ))}
                                </div>
                                {photoFiles.length > 5 && (
                                  <div className="v3-photo-warn">
                                    {t.photosWarn(photoFiles.length)}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ fontSize: 12, color: "var(--muted)" }}>{t.noPhotosInZip}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: 10 }}>
                    {/* Data files */}
                    <div>
                      <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.productFiles}</div>
                      {dataFiles.length === 0 ? (
                        <button type="button" className="v3-drop" onClick={() => fileInputRef.current?.click()}>
                          <div className="v3-drop-inner" style={{ padding: "24px 16px" }}>
                            <div className="v3-drop-icon">{I.excel}</div>
                            <div className="v3-drop-lbl" style={{ fontSize: 14 }}>{t.uploadFile}</div>
                            <div className="v3-drop-hint">XLSX · PDF · DOCX · max 25 MB na plik</div>
                          </div>
                        </button>
                      ) : (
                        <div>
                          {dataFiles.map((f) => (
                            <div key={f.id} className="v3-fpill">
                              <span className={"v3-ext" + (f.filename.endsWith(".xlsx") ? " xlsx" : "")}>{fileExtension(f.filename)}</span>
                              <span className="v3-fnm">{f.filename}</span>
                              <span className="v3-fsz mono">{formatBytes(f.byte_size)}</span>
                              <button className="v3-fx" onClick={() => router.delete(`/products/${product.id}/product_files/${f.id}`)}>{I.x}</button>
                            </div>
                          ))}
                          <button className="v3-add-more" onClick={() => fileInputRef.current?.click()}>
                            {I.plus} {t.addFile}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Photos */}
                    <div>
                      <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.productPhotos}</div>
                      {photoFiles.length === 0 ? (
                        <button type="button" className="v3-drop" onClick={() => photoInputRef.current?.click()}>
                          <div className="v3-drop-inner" style={{ padding: "24px 16px" }}>
                            <div className="v3-drop-icon">{I.img}</div>
                            <div className="v3-drop-lbl" style={{ fontSize: 14 }}>{t.uploadPhotos}</div>
                            <div className="v3-drop-hint">JPG · PNG · WEBP · max 25 MB na plik</div>
                          </div>
                        </button>
                      ) : (
                        <div>
                          <div className="v3-photo-grid">
                            {photoFiles.map((photoFile) => (
                              <div key={photoFile.id} className="v3-pg-item">
                                {photoFile.url && <img src={photoFile.url} alt="" />}
                                {photoFile.url && <a href={photoFile.url} target="_blank" rel="noreferrer" className="v3-pg-eye" title={t.preview}>{I.eye}</a>}
                                <button
                                  type="button"
                                  className={"v3-pg-scale" + (photoFile.file_role === "scale_reference" ? " active" : "")}
                                  title={photoFile.file_role === "scale_reference" ? t.clearScaleReference : t.setScaleReference}
                                  aria-label={`${photoFile.file_role === "scale_reference" ? t.clearScaleReference : t.setScaleReference}: ${photoFile.filename}`}
                                  aria-pressed={photoFile.file_role === "scale_reference"}
                                  onClick={() => photoFile.file_role === "scale_reference"
                                    ? updateFileRole(photoFile.id, "photo")
                                    : setScaleReference(photoFile.id)}
                                >
                                  {I.ruler}
                                </button>
                                <button className="v3-pg-del" title={c.actions.delete} aria-label={`${c.actions.delete}: ${photoFile.filename}`} onClick={() => router.delete(`/products/${product.id}/product_files/${photoFile.id}`)}>{I.x}</button>
                              </div>
                            ))}
                            <div className="v3-pg-item" style={{ border: "1.5px dashed var(--line)", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted-2)" }} onClick={() => photoInputRef.current?.click()}>
                              {I.plus}
                            </div>
                          </div>
                          <div className="v3-photo-hint">{t.scaleReferenceHint}</div>
                          {photoFiles.length > 5 && (
                            <div className="v3-photo-warn">
                              {t.photosWarn(photoFiles.length)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(uploading || importing || processing) && (
                  <div className="v3-progress" role="status" aria-live="polite">
                    <div className="v3-progress-lbl">
                      <span className="v3-spin" />
                      {processing || (importing ? t.urlImporting : t.uploading)}
                    </div>
                    <div className="v3-progress-bar"><span /></div>
                  </div>
                )}

                <div className="v3-btn-row" style={{ flexWrap: "wrap", rowGap: 6 }}>
                  {canAnalyze && (
                    <button className="btn btn-accent" onClick={handleParse} disabled={parsing} style={{ height: 40 }}>
                      {parsing ? <span className="v3-spin" /> : I.spark}
                      {parsing ? ` ${t.analyzing}` : isParsed ? ` ${t.analyzeAgain}` : ` ${t.analyze}`}
                    </button>
                  )}
                  {canAnalyze && (
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>
                      {product.token_usage?.product_parse
                        ? t.lastAnalysis(formatTokensPl(product.token_usage.product_parse.total_tokens), product.token_usage.product_parse.model)
                        : t.usesGeminiTokens}
                    </span>
                  )}
                  <button className="btn btn-primary" style={{ height: 40, marginLeft: "auto" }} onClick={confirmUpload}>
                    {isProductCardReviewEnabled ? t.nextProductCard : t.nextChooseVisuals}
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h12M10 4l6 6-6 6"/></svg>
                  </button>
                </div>
                </fieldset>
              </div>
            </div>
          )}

          {uploadDone && !uploadOpen && (
            <div className="v3-sec-summary" onClick={editUpload}>
              <span className="v3-sum-step mono">01 · UPLOAD</span>
              <div className="v3-sum-content">
                <span className="v3-sum-title">{t.filesUploaded}</span>
                <div className="v3-sum-tags">
                  {dataFiles.map((f) => (
                    <span key={f.id} className="v3-sum-tag">{f.filename.split("/").pop()}</span>
                  ))}
                  {photoFiles.length > 0 && (
                    <span className="v3-sum-tag">{t.photosCount(photoFiles.length)}</span>
                  )}
                </div>
              </div>
              <span className="v3-sum-badge"><Chk /> {t.ready}</span>
              <span className="v3-sum-div" />
              <button className="v3-sum-edit" onClick={(e) => { e.stopPropagation(); editUpload(); }}>{t.edit}</button>
            </div>
          )}
        </div>

        {isProductCardReviewEnabled && (
          <div className="v3-sec" ref={secProduct}>
            {!uploadDone && !productOpen && (
              <div className="v3-sec-locked">
                <span className="v3-locked-num mono">02</span>
                <span className="v3-locked-lbl">{t.productLocked}</span>
              </div>
            )}

            {productOpen && (
              <div className="v3-sec-open v3-slide-in">
                <div className="v3-sec-hd">
                  <span className="v3-sec-num mono">{t.step} 02</span>
                  <div>
                    <h2>{t.reviewProductCard}</h2>
                    <p>{t.reviewSubtitle}</p>
                  </div>
                </div>
                <div className="v3-sec-body">
                {parsing ? (
                  <div className="v3-parse-wrap">
                    <div className="v3-spin-lg" />
                    <div className="mono" style={{ fontSize: 11, color: "var(--muted)", letterSpacing: ".06em" }}>PARSING…</div>
                    <div style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 8 }}>
                      {[80, 60, 90, 55, 70].map((w, i) => (
                        <div key={i} className="v3-skel" style={{ width: w + "%", animationDelay: i * 0.08 + "s" }} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Product metadata - editable */}
                    <div className="v3-pc-card">
                      <div className="v3-pcf">
                        <div className="v3-pclbl">{t.name}</div>
                        <div className="v3-pcval">
                          <input
                            className="v3-input"
                            style={{ width: "100%", fontWeight: 500 }}
                            value={nameEdit}
                            onChange={(e) => setNameEdit(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="v3-pcf product-dimensions-field">
                        <div className="v3-pclbl">{t.dimensions}</div>
                        <div className="v3-pcval product-dimensions">
                          {Object.entries(dimensionsEdit).map(([k, v]) => (
                            <div key={k} className="product-dimensions__row">
                              <span className="mono product-dimensions__label" title={k}>
                                {dimensionKeyLabel(k)}
                              </span>
                              <input
                                className="v3-input"
                                style={{ width: "100%" }}
                                value={v}
                                aria-label={dimensionKeyLabel(k)}
                                onChange={(e) => setDimensionsEdit((prev) => ({ ...prev, [k]: e.target.value }))}
                              />
                              <button
                                type="button"
                                onClick={() => removeDimensionRow(k)}
                                style={{ background: "none", border: "none", padding: "0 4px", cursor: "pointer", color: "var(--muted)", fontSize: 16, lineHeight: 1 }}
                                title={t.deleteDimension}
                              >×</button>
                            </div>
                          ))}

                          <DimensionReviewNotice
                            items={product.dimension_review ?? []}
                            files={product.files}
                            values={reviewDimensionValues}
                            onChange={(key, value) => setReviewDimensionValues((prev) => ({ ...prev, [key]: value }))}
                            resolvedKeys={resolvedReviewKeys}
                            onAccept={acceptDimensionReview}
                            onReject={rejectDimensionReview}
                          />

                          <div className="product-dimensions__add">
                            <select
                              className="v3-select"
                              style={{ width: 200, height: 26, fontSize: 12, flexShrink: 0 }}
                              value={selectedDimKey}
                              aria-label={t.dimensions}
                              onChange={(e) => setNewDimKeySelect(e.target.value)}
                            >
                              <option value="">{t.chooseDimension}</option>
                              {availableDimOptions.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                              <option value="__custom">{t.customKey}</option>
                            </select>
                            {selectedDimKey === "__custom" && (
                              <input
                                className="v3-input mono"
                                style={{ width: 130, height: 26, fontSize: 12 }}
                                placeholder={t.customKeyPlaceholder}
                                aria-label={t.customKeyPlaceholder}
                                value={newDimCustomKey}
                                onChange={(e) => setNewDimCustomKey(e.target.value)}
                              />
                            )}
                            <input
                              className="v3-input"
                              style={{ flex: 1, height: 26, fontSize: 12 }}
                              placeholder={t.valuePlaceholder}
                              aria-label={t.valuePlaceholder}
                              value={newDimValue}
                              onChange={(e) => setNewDimValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addDimensionRow();
                                }
                              }}
                            />
                            <button
                              type="button"
                              onClick={addDimensionRow}
                              disabled={!selectedDimKey || !newDimValue.trim() || (selectedDimKey === "__custom" && !newDimCustomKey.trim())}
                              style={{ background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 4, padding: "4px 10px", cursor: "pointer", fontSize: 12, height: 26, flexShrink: 0 }}
                            >{t.addShort}</button>
                          </div>
                        </div>
                      </div>

                      <div className="v3-pcf">
                        <div className="v3-pclbl">{t.variants}</div>
                        <div className="v3-pcval" style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                          {variantsEdit.map((v, i) => (
                            <span key={i} className="v3-ptag" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                              {v.name}
                              <button
                                type="button"
                                onClick={() => setVariantsEdit((prev) => prev.filter((_, j) => j !== i))}
                                style={{ background: "none", border: "none", padding: "0 0 0 2px", cursor: "pointer", color: "var(--muted)", fontSize: 14, lineHeight: 1 }}
                              >×</button>
                            </span>
                          ))}
                          <input
                            className="v3-input"
                            style={{ width: 140, height: 26, fontSize: 12 }}
                            placeholder={t.addVariant}
                            value={newVariantInput}
                            onChange={(e) => setNewVariantInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newVariantInput.trim()) {
                                e.preventDefault();
                                setVariantsEdit((prev) => [...prev, { name: newVariantInput.trim() }]);
                                setNewVariantInput("");
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div className="v3-pcf">
                        <div className="v3-pclbl">{t.certificates}</div>
                        <div className="v3-pcval" style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                          {certificationsEdit.map((c, i) => (
                            <span key={i} className="v3-ptag mono" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                              {c}
                              <button
                                type="button"
                                onClick={() => setCertificationsEdit((prev) => prev.filter((_, j) => j !== i))}
                                style={{ background: "none", border: "none", padding: "0 0 0 2px", cursor: "pointer", color: "var(--muted)", fontSize: 14, lineHeight: 1 }}
                              >×</button>
                            </span>
                          ))}
                          <input
                            className="v3-input"
                            style={{ width: 120, height: 26, fontSize: 12 }}
                            placeholder={t.addCert}
                            value={newCertInput}
                            onChange={(e) => setNewCertInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newCertInput.trim()) {
                                e.preventDefault();
                                setCertificationsEdit((prev) => [...prev, newCertInput.trim()]);
                                setNewCertInput("");
                              }
                            }}
                          />
                        </div>
                      </div>

                      <div className="v3-pcf">
                        <div className="v3-pclbl">{t.features}</div>
                        <div className="v3-pcval" style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                          {featuresEdit.map((f, i) => (
                            <span key={i} className="v3-ptag" style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                              {f}
                              <button
                                type="button"
                                onClick={() => setFeaturesEdit((prev) => prev.filter((_, j) => j !== i))}
                                style={{ background: "none", border: "none", padding: "0 0 0 2px", cursor: "pointer", color: "var(--muted)", fontSize: 14, lineHeight: 1 }}
                              >×</button>
                            </span>
                          ))}
                          <input
                            className="v3-input"
                            style={{ width: 160, height: 26, fontSize: 12 }}
                            placeholder={t.addFeature}
                            value={newFeatureInput}
                            onChange={(e) => setNewFeatureInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newFeatureInput.trim()) {
                                e.preventDefault();
                                setFeaturesEdit((prev) => [...prev, newFeatureInput.trim()]);
                                setNewFeatureInput("");
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Editable description */}
                    <div style={{ marginTop: 16 }}>
                      <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.productDescription}</div>
                      <textarea
                        className="v3-textarea"
                        rows={8}
                        value={descEdit}
                        onChange={(e) => setDescEdit(e.target.value)}
                        placeholder={t.descPlaceholder}
                      />
                    </div>

                    {/* Visual fingerprint (interactive) */}
                    <details style={{ marginTop: 16, padding: "10px 12px", background: "var(--panel-2, var(--panel))", border: "1px solid var(--border)", borderRadius: 6 }} open={!!fingerprintError || fingerprintEdit !== null}>
                      <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                        Visual fingerprint <span style={{ color: "var(--muted)", fontWeight: 400 }}>{t.fingerprintHint}</span>
                      </summary>

                      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <button
                          type="button"
                          className="v3-btn"
                          disabled={reanalyzing || product.files.filter((f) => f.content_type.startsWith("image/")).length === 0}
                          onClick={async () => {
                            setReanalyzing(true);
                            setFingerprintError(null);
                            try {
                              await postWithoutBody(`/products/${product.id}/reanalyze`);
                            } catch (err) {
                              setReanalyzing(false);
                              setFingerprintError(err instanceof Error ? err.message : t.analyzeStartFailed);
                            }
                          }}
                        >
                          {reanalyzing ? t.analyzing : t.analyzePhotosAgain}
                        </button>
                        {fingerprintEdit === null && !product.visual_analyzed_at && (
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>{t.usesGeminiTokens}</span>
                        )}
                        {fingerprintEdit === null ? (
                          <button
                            type="button"
                            className="v3-btn"
                            disabled={!product.visual_analysis_md || reanalyzing}
                            onClick={() => setFingerprintEdit(product.visual_analysis_md || "")}
                          >
                            {t.edit}
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="v3-btn v3-btn-accent"
                              disabled={savingFingerprint}
                              onClick={async () => {
                                if (fingerprintEdit === null) return;
                                setSavingFingerprint(true);
                                try {
                                  await router.patch(
                                    `/products/${product.id}`,
                                    { product: { visual_analysis_md: fingerprintEdit } },
                                    {
                                      preserveScroll: true,
                                      onSuccess: () => {
                                        setFingerprintEdit(null);
                                        setFingerprintError(null);
                                      },
                                      onFinish: () => setSavingFingerprint(false),
                                    }
                                  );
                                } catch (err) {
                                  setSavingFingerprint(false);
                                  setFingerprintError(err instanceof Error ? err.message : t.saveFailed);
                                }
                              }}
                            >
                              {savingFingerprint ? t.saving : t.save}
                            </button>
                            <button
                              type="button"
                              className="v3-btn"
                              disabled={savingFingerprint}
                              onClick={() => { setFingerprintEdit(null); setFingerprintError(null); }}
                            >
                              {t.cancel}
                            </button>
                          </>
                        )}
                        {product.visual_analyzed_at && fingerprintEdit === null && (
                          <span style={{ fontSize: 11, color: "var(--muted)" }}>
                            {t.lastTime(new Date(product.visual_analyzed_at).toLocaleString(getLanguage() === "en" ? "en-GB" : "pl-PL"), product.token_usage?.visual_analysis ? t.tokensExtra(formatTokensPl(product.token_usage.visual_analysis.total_tokens), product.token_usage.visual_analysis.model) : "")}
                          </span>
                        )}
                      </div>

                      {fingerprintError && (
                        <div style={{ marginTop: 8, fontSize: 12, color: "var(--danger, #c33)" }}>{fingerprintError}</div>
                      )}

                      {fingerprintEdit !== null ? (
                        <textarea
                          className="v3-textarea"
                          rows={16}
                          value={fingerprintEdit}
                          onChange={(e) => setFingerprintEdit(e.target.value)}
                          style={{ marginTop: 10, fontSize: 11, lineHeight: 1.5, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                        />
                      ) : product.visual_analysis_md ? (
                        <pre style={{ marginTop: 10, fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap", color: "var(--muted)", maxHeight: 320, overflowY: "auto" }}>
                          {product.visual_analysis_md}
                        </pre>
                      ) : (
                        <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
                          {reanalyzing ? t.analyzingPhotos : t.noFingerprint}
                        </div>
                      )}
                    </details>

                    {/* Global settings */}
                    <div style={{ marginTop: 20, padding: "16px", background: "var(--panel-2, var(--panel))", borderRadius: 6, border: "1px solid var(--border)" }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14 }}>{t.generationSettings}</div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <div>
                          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.productCategory}</div>
                          <select
                            className="v3-select"
                            value={contextKey}
                            onChange={(e) => setContextKey(e.target.value)}
                          >
                            {contextOptions().map((o) => (
                              <option key={o.k} value={o.k}>{o.n}</option>
                            ))}
                          </select>
                          {contextKey === "custom" && (
                            <input
                              type="text"
                              className="v3-input"
                              style={{ marginTop: 6 }}
                              placeholder={t.customCategoryPlaceholder}
                              value={contextCustom}
                              onChange={(e) => setContextCustom(e.target.value)}
                            />
                          )}
                        </div>

                        <div>
                          <div className="v3-field-label" style={{ marginBottom: 8 }}>{t.captionsOnBoards}</div>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={captionsOn}
                              onChange={(e) => setCaptionsOn(e.target.checked)}
                              style={{ width: 14, height: 14 }}
                            />
                            {t.generateCaptions}
                          </label>
                          {captionsOn && (
                            <input
                              type="text"
                              className="v3-input"
                              style={{ marginTop: 8 }}
                              placeholder={t.fontPlaceholder}
                              value={captionFont}
                              onChange={(e) => setCaptionFont(e.target.value)}
                            />
                          )}
                        </div>
                      </div>

                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <div>
                          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.imageOrientation}</div>
                          <select
                            className="v3-select"
                            value={globalAspectRatio}
                            onChange={(e) => setGlobalAspectRatio(e.target.value)}
                          >
                            {image_aspect_ratios.map((o) => (
                              <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.qualityResolution}</div>
                          <select
                            className="v3-select"
                            value={globalImageSize}
                            onChange={(e) => setGlobalImageSize(e.target.value)}
                          >
                            {image_sizes.map((o) => (
                              <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {isQualityGateEnabled && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={qualityGateOn}
                              onChange={(e) => setQualityGateOn(e.target.checked)}
                              style={{ width: 14, height: 14 }}
                            />
                            {t.aiQualityControl}
                          </label>
                          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 6 }}>
                            {t.qualityControlHint}
                          </div>
                        </div>
                      )}

                      {areBrandsEnabled && brands.length > 0 && (
                        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                          <div className="v3-field-label" style={{ marginBottom: 8 }}>{t.brand}</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "flex-start" }}>
                            <select
                              className="v3-select"
                              value={brandId ?? ""}
                              onChange={(e) => setBrandId(e.target.value ? Number(e.target.value) : null)}
                            >
                              <option value="">{t.noBrand}</option>
                              {brands.map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                              ))}
                            </select>
                            {brandId && (
                              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap", paddingTop: 6 }}>
                                <input
                                  type="checkbox"
                                  checked={includeBrandLogo}
                                  onChange={(e) => setIncludeBrandLogo(e.target.checked)}
                                  style={{ width: 14, height: 14 }}
                                />
                                {t.includeBrandLogo}
                              </label>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="v3-btn-row">
                      <button className="btn btn-ghost btn-sm" onClick={saveProductCard} disabled={savingDesc} style={{ height: 38 }}>
                        {savingDesc ? <span className="v3-spin" /> : null} {t.saveChanges}
                      </button>
                      <button className="btn btn-primary" style={{ height: 40, marginLeft: "auto" }} onClick={confirmProduct}>
                        {t.chooseVisuals}
                        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h12M10 4l6 6-6 6"/></svg>
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </div>
            )}

            {productDone && (
              <div className="v3-sec-summary" onClick={editProduct}>
                <span className="v3-sum-step mono">02 · PRODUKT</span>
                <div className="v3-sum-content">
                  <span className="v3-sum-title">{product.name || t.productWord}</span>
                  {product.variants_json && product.variants_json.length > 0 && (
                    <span className="v3-sum-desc">· {t.variantsCount(product.variants_json.length)}</span>
                  )}
                </div>
                <span className="v3-sum-badge"><Chk /> {t.ready}</span>
                <span className="v3-sum-div" />
                <button className="v3-sum-edit" onClick={(e) => { e.stopPropagation(); editProduct(); }}>{t.edit}</button>
              </div>
            )}
          </div>
        )}

        {/* ── Section 3: Modes ── */}
        <div className="v3-sec" ref={secModes}>
          {!productDone && !modesOpen && (
            <div className="v3-sec-locked">
              <span className="v3-locked-num mono">{isProductCardReviewEnabled ? "03" : "02"}</span>
              <span className="v3-locked-lbl">
                {isProductCardReviewEnabled ? t.modesLockedReview : t.modesLockedUpload}
              </span>
            </div>
          )}

          {modesOpen && (
            <div className="v3-sec-open v3-slide-in">
              <div className="v3-sec-hd">
                <span className="v3-sec-num mono">{t.step} {isProductCardReviewEnabled ? "03" : "02"}</span>
                <div>
                  <h2>{t.whatGenerate}</h2>
                  <p>{t.modesSubtitle}</p>
                </div>
              </div>
              <div className="v3-sec-body">
                <div className="v3-sel-row">
                  <span className="v3-sel-cnt">{t.selectedLabel} <b>{selected.size}</b> {t.ofWord} {allModes.length}</span>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="v3-link-b" onClick={() => setSelected(new Set(allModes.map((m) => m.k)))}>{t.selectAll}</button>
                    <button className="v3-link-b" onClick={() => setSelected(new Set())}>{t.deselect}</button>
                  </div>
                </div>
                <div className="v3-modes">
                  {allModes.map((m) => {
                    const on = selected.has(m.k);
                    const estimate = estimateImageCalls(m.k, modeConfigs[m.k], product);
                    const tipId = `mtip-${m.k}`;
                    return (
                      <div key={m.k} className="v3-mtile-wrap">
                        <button type="button" className={"v3-mtile" + (on ? " on" : "")} aria-pressed={on} aria-describedby={tipId} onClick={() => toggleMode(m.k)}>
                          <div className="v3-mthumb" style={{ background: m.color }} />
                          <div className="v3-mlbl">
                            {m.n}
                            <small>{m.d}</small>
                          </div>
                          {estimate > 1 && <span className="v3-mbadge">×{estimate}</span>}
                          <span className={"v3-mchk" + (on ? " on" : "")}>
                            {on && <Chk />}
                          </span>
                        </button>
                        <span role="tooltip" id={tipId} className="v3-mtip">
                          {m.description}
                          <span className="v3-mtip-est">{graphicsEstimateLabel(estimate)}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
                {totalImageEstimate >= 8 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted)", lineHeight: 1.5 }}>
                    {t.tokenHint}
                  </div>
                )}

                {arePersonasEnabled && (
                  <div style={{ marginTop: 14, padding: "16px", background: "var(--panel-2, var(--panel))", borderRadius: 6, border: "1px solid var(--border)" }}>
                    <div className="v3-field-label" style={{ marginBottom: 10 }}>{t.modelPersona}</div>
                    <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                        <input
                          type="radio"
                          name="persona-mode"
                          checked={personaMode === "none"}
                          onChange={() => setPersonaMode("none")}
                        />
                        {t.personaModeNone}
                      </label>
                      {model_personas.length > 0 && (
                        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                          <input
                            type="radio"
                            name="persona-mode"
                            checked={personaMode === "saved"}
                            onChange={() => setPersonaMode("saved")}
                          />
                          {t.personaModeSaved}
                        </label>
                      )}
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                        <input
                          type="radio"
                          name="persona-mode"
                          checked={personaMode === "inline"}
                          onChange={() => setPersonaMode("inline")}
                        />
                        {t.personaModeInline}
                      </label>
                    </div>

                    {personaMode === "saved" && (
                      <div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>{t.personaSavedHint}</div>
                        <select
                          className="v3-select"
                          value={personaId ?? ""}
                          onChange={(e) => setPersonaId(e.target.value ? Number(e.target.value) : null)}
                        >
                          <option value="">{t.noModelPersona}</option>
                          {model_personas.map((persona) => (
                            <option key={persona.id} value={persona.id}>{persona.name}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {personaMode === "inline" && (
                      <div>
                        <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10 }}>{t.personaInlineHint}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                          <div>
                            <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.personaSkinTone}: {personaSkinTone}/10</div>
                            <input
                              type="range"
                              min={1}
                              max={10}
                              step={1}
                              value={personaSkinTone}
                              onChange={(e) => setPersonaSkinTone(Number(e.target.value))}
                              style={{ width: "100%" }}
                            />
                          </div>
                          <div>
                            <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.personaAgeBand}</div>
                            <select
                              className="v3-select"
                              value={personaAgeBand}
                              onChange={(e) => setPersonaAgeBand(e.target.value as AgeBand)}
                            >
                              {PERSONA_AGE_BANDS.map((band) => (
                                <option key={band} value={band}>{t.ageBandLabels[band] ?? band}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.personaPresentation}</div>
                            <select
                              className="v3-select"
                              value={personaPresentation}
                              onChange={(e) => setPersonaPresentation(e.target.value as PersonaPresentation)}
                            >
                              {PERSONA_PRESENTATIONS.map((value) => (
                                <option key={value} value={value}>{t.presentationLabels[value] ?? value}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.personaHandsStyling}</div>
                            <select
                              className="v3-select"
                              value={personaHandsStyling}
                              onChange={(e) => setPersonaHandsStyling(e.target.value as HandsStyling)}
                            >
                              {PERSONA_HANDS_STYLINGS.map((value) => (
                                <option key={value} value={value}>{t.handsStylingLabels[value] ?? value}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.personaHairStyling}</div>
                            <select
                              className="v3-select"
                              value={personaHairStyling}
                              onChange={(e) => setPersonaHairStyling(e.target.value as HairStyling)}
                            >
                              {PERSONA_HAIR_STYLINGS.map((value) => (
                                <option key={value} value={value}>{t.hairStylingLabels[value] ?? value}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.personaBodyFocus}</div>
                            <select
                              className="v3-select"
                              value={personaBodyFocus}
                              onChange={(e) => setPersonaBodyFocus(e.target.value as BodyFocus)}
                            >
                              {PERSONA_BODY_FOCUSES.map((value) => (
                                <option key={value} value={value}>{t.bodyFocusLabels[value] ?? value}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div style={{ marginTop: 14 }}>
                          <div className="v3-field-label" style={{ marginBottom: 6 }}>{t.personaAppearanceNotesOpt}</div>
                          <textarea
                            className="v3-textarea"
                            rows={2}
                            maxLength={PERSONA_APPEARANCE_NOTES_MAX_LENGTH}
                            value={personaAppearanceNotes}
                            onChange={(e) => setPersonaAppearanceNotes(e.target.value)}
                            placeholder={t.personaAppearanceNotesPlaceholder}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Per-mode config panels */}
                {selectedConfigurable.length > 0 && (
                  <>
                  <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "var(--muted)" }}>
                      {product.token_usage?.config_suggestions
                        ? t.lastSuggestion(formatTokensPl(product.token_usage.config_suggestions.total_tokens), product.token_usage.config_suggestions.model)
                        : t.usesGeminiTokens}
                    </span>
                    <button
                      type="button"
                      onClick={handleSuggestConfig}
                      disabled={isSuggestingConfig}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500,
                        border: "1px solid var(--accent)", background: "var(--accent-soft)",
                        color: "var(--accent)", cursor: "pointer", transition: "opacity .15s",
                        opacity: isSuggestingConfig ? 0.6 : 1,
                      }}
                    >
                      {isSuggestingConfig
                        ? <><span className="v3-spin" style={{ width: 11, height: 11 }} /> {t.suggestingAI}</>
                        : <><span style={{ animation: "v3-diamond-pulse 2s ease-in-out infinite", display: "inline-block" }}>◆</span> {t.suggestScenesAI}</>
                      }
                    </button>
                  </div>
                  <details className="v3-mode-config" style={{ marginTop: 8 }}>
                    <summary className="v3-mode-config-hd" style={{ fontWeight: 600, fontSize: 12, letterSpacing: ".06em", textTransform: "uppercase" }}>
                      <span className="v3-mode-config-name">{t.modeConfiguration}</span>
                      <span className="v3-mode-config-chevron">›</span>
                    </summary>
                    <div className="v3-mode-config-body" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {selectedConfigurable.map((modeKey) => {
                        const modeMeta = allModes.find((m) => m.k === modeKey)!;
                        return (
                          <details key={modeKey} className="v3-mode-config">
                            <summary className="v3-mode-config-hd">
                              <span className="v3-mode-config-dot" style={{ background: modeMeta.color }} />
                              <span className="v3-mode-config-name">{modeMeta.n}</span>
                              <span className="v3-mode-config-chevron">›</span>
                            </summary>
                            <div className="v3-mode-config-body">
                              <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                                <select
                                  className="v3-select"
                                  style={{ flex: 1 }}
                                  value={modeConfigs[modeKey]?.aspect_ratio || ""}
                                  onChange={(e) => updateModeConfigInheritable(modeKey, "aspect_ratio", e.target.value)}
                                >
                                  <option value="">{t.inheritOrientation}</option>
                                  {image_aspect_ratios.map((o) => (
                                    <option key={o.id} value={o.id}>{o.label}</option>
                                  ))}
                                </select>
                                <select
                                  className="v3-select"
                                  style={{ flex: 1 }}
                                  value={modeConfigs[modeKey]?.image_size || ""}
                                  onChange={(e) => updateModeConfigInheritable(modeKey, "image_size", e.target.value)}
                                >
                                  <option value="">{t.inheritQuality}</option>
                                  {image_sizes.map((o) => (
                                    <option key={o.id} value={o.id}>{o.label}</option>
                                  ))}
                                </select>
                              </div>
                              <ModeConfigPanel
                                modeKey={modeKey}
                                config={modeConfigs[modeKey] || {}}
                                onChange={(k, v) => updateModeConfig(modeKey, k, v)}
                                product={product}
                              />
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </details>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Job history ── */}
        {product.generation_jobs.length > 0 && (
          <div className="v3-sec" style={{ marginTop: 8 }}>
            <div style={{ padding: "16px 20px" }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: "var(--muted)", letterSpacing: ".06em", marginBottom: 10 }}>{t.previousGenerations}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {product.generation_jobs.map((j) => (
                  <div
                    key={j.id}
                    style={{ display: "flex", alignItems: "center", background: "var(--panel)", border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden" }}
                  >
                    <a
                      href={`/products/${product.id}/generation_jobs/${j.id}`}
                      style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", flex: 1, textDecoration: "none", color: "inherit", minWidth: 0 }}
                    >
                      <span style={{ fontSize: 11, color: "var(--muted)", fontFamily: "monospace", flexShrink: 0 }}>
                        {new Date(j.created_at).toLocaleString(getLanguage() === "en" ? "en-GB" : "pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, flex: 1 }}>
                        {(j.modes || []).map((m) => (
                          <span key={m} style={{ fontSize: 11, padding: "1px 6px", background: "var(--bg-2, var(--bg))", border: "1px solid var(--border)", borderRadius: 3 }}>{modeLabel(m)}</span>
                        ))}
                      </div>
                      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 10h12M10 4l6 6-6 6"/></svg>
                    </a>
                    <button
                      type="button"
                      aria-label={t.deleteGeneration}
                      onClick={() => {
                        if (confirm(t.confirmDeleteGeneration)) {
                          router.delete(`/products/${product.id}/generation_jobs/${j.id}`, { preserveScroll: true });
                        }
                      }}
                      style={{ flexShrink: 0, padding: "8px 12px", background: "transparent", border: "none", borderLeft: "1px solid var(--border)", color: "var(--muted)", cursor: "pointer", display: "flex", alignItems: "center" }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "var(--danger)"; (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.20 25 / 0.06)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "var(--muted)"; (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                    >
                      <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h14M8 6V4h4v2M5 6l1 11a1 1 0 001 1h6a1 1 0 001-1l1-11"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Section 4: Results ── */}
        {activeJob && (
          <div ref={secResults}>
            <InlineResultsSection job={activeJob} productId={product.id} />
          </div>
        )}

      </div>{/* /v3-page */}

      {/* ── Sticky generate bar ── */}
      <div className={"v3-gen-bar" + (modesOpen && selected.size > 0 ? " visible" : "")}>
        <div className="v3-gen-bar-info">
          <div className="v3-gen-bar-t">{t.readyToGenerate}</div>
          <div className="v3-gen-bar-s mono">{t.genBarSummary(selected.size, totalImageEstimate)} · {product.name}</div>
        </div>
        <button className="btn btn-accent" style={{ height: 46, fontSize: 14, fontWeight: 600, padding: "0 24px" }}
          onClick={handleGenerate} disabled={selected.size === 0 || generating}>
          {generating ? <span className="v3-spin" style={{ marginRight: 6 }} /> : I.spark}
          {generating ? t.starting : `${t.generate}${selected.size > 0 ? ` · ${selected.size}` : ""}`}
        </button>
      </div>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </AppShell>
  );
}
